import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faHome, faFileAlt, faCog,
  faSignOutAlt, faPlusSquare, faUser, faPaperPlane,
  faChevronDown, faChevronRight, faBalanceScale, faBell, 
  faTimesCircle, faInbox, faTimes
} from '@fortawesome/free-solid-svg-icons';
import { FaFolderOpen, FaCalendarAlt, FaUserTie, FaMapMarkedAlt } from 'react-icons/fa';
import HistoriqueDepenses from '../components/HistoriqueDepenses';
import { navigateToHistoriqueDepenses } from '../components/NavigateUtil';
import FiltreDepensesChef from '../components/FiltreDepensesChef';
import pb from '../api/pocketbase';
import { createDepense, updateDepenseMEA } from '../services/depenseService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const ProjectIdValidator = ({ onValidProject }) => {
  const [projectId, setProjectId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setProjectId(e.target.value);
    setError('');
  };

  const validateProjectId = async (e) => {
    e.preventDefault();

    if (!projectId.trim()) {
      setError('Veuillez entrer un ID de projet');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const projet = await pb.collection('projects').getOne(projectId.trim());
      if (projet && projet.id) {
        if (onValidProject) {
          onValidProject(projet);
        }
      }
    } catch (err) {
      if (err.status === 404) {
        setError('Aucun projet trouvé avec cet ID. Veuillez vérifier et réessayer.');
      } else if (err.status === 401 || err.status === 403) {
        setError("Problème d'authentification. Veuillez vous reconnecter.");
      } else {
        setError('Une erreur est survenue. Vérifiez votre connexion ou l\'ID du projet.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20 p-6 bg-white rounded-lg shadow-lg border border-gray-200">
      <div className="text-center mb-6">
        <img src="dywanes-logo.png" alt="Dywane Logo" className="h-16 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800">Accès Chef de Chantier</h2>
        <p className="text-gray-600 mt-2">Veuillez entrer l'ID du projet pour continuer</p>
      </div>
      
      <form onSubmit={validateProjectId} className="space-y-4">
        <div>
          <label htmlFor="projectId" className="block text-sm font-medium text-gray-700 mb-1">
            ID du Projet
          </label>
          <input
            type="text"
            id="projectId"
            value={projectId}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Entrez l'ID du projet"
          />
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-3 px-4 rounded-md text-white font-medium ${
            isLoading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
          } transition-colors flex justify-center items-center`}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Vérification...
            </>
          ) : (
            'Accéder au projet'
          )}
        </button>
      </form>
      
      {error && (
        <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}
    </div>
  );
};

const ChefChantierDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    if (!pb.authStore.isValid) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const [projectData, setProjectData] = useState(null);
  const [rubrique, setRubrique] = useState('');
  const [sousRubrique, setSousRubrique] = useState('');
  const [montantDepense, setMontantDepense] = useState('');
  const [fournisseur, setFournisseur] = useState('');
  const [justificatif, setJustificatif] = useState(null);
  const [depenses, setDepenses] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedRubriques, setExpandedRubriques] = useState({});
  const [justificatifFile, setJustificatifFile] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [currentComponent, setCurrentComponent] = useState(null);
  const [description, setDescription] = useState('');
  const [tempData, setTempData] = useState([]);
  const [chefsDisponibles, setChefsDisponibles] = useState([]);
  const [chefChantierNom, setChefChantierNom] = useState('');
  const [notifCount, setNotifCount] = useState(0);
  const [depenseAModifier, setDepenseAModifier] = useState(null);
  const [waitingSetter, setWaitingSetter] = useState(null);
  const [montantAffiche, setMontantAffiche] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // --- Ajout de la gestion des notifications en temps réel ---
  useEffect(() => {
    if (!projectData) return;

    let unsubWaiting = null;
    let unsubValidated = null;

    const getSeenWaitingKey = () => `seenWaitingNotifications_chef_${projectData.id}`;
    const getSeenValidatedKey = () => `seenValidatedNotifications_chef_${projectData.id}`;

    const getSeenIds = (key) => {
      try {
        const item = localStorage.getItem(key);
        return item ? new Set(JSON.parse(item)) : new Set();
      } catch (e) {
        return new Set();
      }
    };

    const addSeenIds = (key, idsToAdd) => {
      if (!idsToAdd || idsToAdd.size === 0) return;
      const seenIds = getSeenIds(key);
      idsToAdd.forEach(id => seenIds.add(id));
      localStorage.setItem(key, JSON.stringify(Array.from(seenIds)));
    };

    const [waiting, setWaiting] = [[], () => {}];
    const [validated, setValidated] = [[], () => {}];

    const handleWaitingUpdate = (e) => {
      const { action, record } = e;
      if (record.nomProjet !== projectData.nom) return;

      const isRelevant = record.meaPar === "Responsable Technique" && record.status !== "traité";

      setWaiting(prev => {
        const index = prev.findIndex(item => item.id === record.id);
        const exists = index !== -1;

        if (action === 'delete') {
          const newList = exists ? prev.filter(item => item.id !== record.id) : prev;
          setNotifCount(newList.length + validated.length);
          return newList;
        }

        if (isRelevant) {
          if (exists) {
            const updatedList = [...prev];
            updatedList[index] = record;
            setNotifCount(updatedList.length + validated.length);
            return updatedList;
          } else {
            const newList = [record, ...prev];
            setNotifCount(newList.length + validated.length);
            return newList;
          }
        } else {
          const newList = exists ? prev.filter(item => item.id !== record.id) : prev;
          setNotifCount(newList.length + validated.length);
          return newList;
        }
      });
    };

    const handleValidatedUpdate = (e) => {
      const { action, record } = e;
      if (record.nomProjet !== projectData.nom) return;

      setValidated(prev => {
        const index = prev.findIndex(item => item.id === record.id);
        const exists = index !== -1;

        if (action === 'delete') {
          const newList = exists ? prev.filter(item => item.id !== record.id) : prev;
          setNotifCount(waiting.length + newList.length);
          return newList;
        }

        if (exists) {
          const updatedList = [...prev];
          updatedList[index] = record;
          setNotifCount(waiting.length + updatedList.length);
          return updatedList.sort((a, b) => new Date(b.dateValidationDG || b.created) - new Date(a.dateValidationDG || a.created));
        }

        const newList = [record, ...prev].sort((a, b) => new Date(b.dateValidationDG || b.created) - new Date(a.dateValidationDG || a.created));
        setNotifCount(waiting.length + newList.length);
        return newList;
      });
    };

    const subscribe = async () => {
      if (!pb.authStore.isValid) {
        console.debug('Subscriptions skipped: not authenticated');
        return;
      }

      try {
        unsubWaiting = await pb.collection('depensemea').subscribe('*', handleWaitingUpdate);
        unsubValidated = await pb.collection('depensesValideesDG').subscribe('*', handleValidatedUpdate);
      } catch (err) {
        console.debug("Subscriptions skipped:", err?.message);
      }
    };

    subscribe();

    return () => {
      if (unsubWaiting) unsubWaiting();
      if (unsubValidated) unsubValidated();
    };
  }, [projectData, setNotifCount]);

  
  const fileInputRef = useRef(null);

  const rubriques = {
    "Terrain": ["Achat terrain", "Enregistrement", "Publication foncière", "Commission"],
    "Gros Œuvre": ["Clôture", "Excavation & démolition", "Travaux"],
    "Architecture & Ingénierie": [
      "Architecture", "Bureau de contrôle", "Assurance décennale", "Urbanisme", "Étude", "Géomètre",
      "Plan BA", "Plan lots techniques", "Notice de sécurité", "Publication règlement de copropriété",
      "Frais de dossier autorisation", "Impôt et taxes", "Taxes diverses / notaire", "Impression & copie plan"
    ],
    "Matériaux de Construction": [
      "Acier & fer", "Ciment", "Gravier & basalte", "Briques", "Sable",
      "Divers matériels & outillage", "Autres"
    ],
    "Aluminium": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Carrelage": ["Fournitures", "Pose", "Fournitures & pose", "Divers", "Quintal"],
    "Cuisines": ["Fournitures", "Pose", "Fournitures & pose", "Divers", "Quintal"],
    "CVC (Climatisation, Ventilation, Chauffage)": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Dressing": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Électricité (CFO + CFA)": [
      "CFO", "CFA", "COSSUEL", "Vidéophone / Domotique", "Fibre optique", "Caméra de surveillance",
      "Fournitures", "Pose", "Consommation électricité", "Frais sur achat", "Quintal"
    ],
    "Étanchéité & Cuvelage": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Faux Plafond (Plâtre / BA13)": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Peinture": ["Fournitures", "Pose", "Fournitures & pose", "Divers"],
    "Plomberie": ["Fournitures", "Pose", "Fournitures & pose", "Divers", "Quintal"],
    "Portes": ["Fournitures", "Pose", "Fournitures & pose", "Divers", "Quintal"],
    "Meubles & Décoration": [
      "Meubles offerts à la résidence", "Enseigne", "Tableau camouflage compteur", "Mobilier jardin",
      "Mobilier terrasse piscine", "Salle polyvalente", "Accessoires décoratifs", "Salon hall d'accueil",
      "Équipement salle de sport", "Pergola", "Desk concierge", "Lambris décoratif"
    ],
    "Machineries": ["Groupe électrogène", "Aquarium", "Suppresseur", "Piscine", "Ascenseur"],
    "Réseaux": ["Poste transformateur", "ONAS", "Ageroute", "Sonatel", "SEN EAU"],
    "Sécurité Incendie": ["Sécurité incendie", "Frais sur achat"],
    "Logistique": ["Container dédouanement", "Container déchargement", "Taxes municipales"],
    "Divers": [
      "Ouvrier & nettoyage", "Divers travaux", "Location matériel chantier", "Réparation matériel",
      "Gardiennage", "Divers matériels & outillage", "Consommation eau/électricité", "Enseigne et décoration"
    ]
  };

  // 🆕 FONCTION pour créer rapidement une dépense avec un formulaire moderne
  const [showModernForm, setShowModernForm] = useState(false);
  const [currentRubrique, setCurrentRubrique] = useState('');
  const [currentSousRubrique, setCurrentSousRubrique] = useState('');

  const handleValidProject = (data) => {
    setProjectData(data);
    setCurrentView('dashboard');
    setCurrentComponent(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setJustificatif(file.name);
      setJustificatifFile(file);
    } else {
      setJustificatif(null);
      setJustificatifFile(null);
    }
  };

  const handleEditDepenseFromNotif = (notif, setWaiting) => {
    setCurrentView('dashboard');
    setShowForm(true);
    setRubrique(notif.rubrique || (notif.rubriques && notif.rubriques[0]?.rubrique) || '');
    setSousRubrique(notif.sousRubrique || (notif.rubriques && notif.rubriques[0]?.sousRubrique) || '');
    setMontantDepense(notif.montant || '');
    setFournisseur(notif.fournisseur || '');
    setDescription(notif.description || '');
    setJustificatif(null);
    setJustificatifFile(null);
    setDepenseAModifier({ ...notif, sourceCollection: "depensemea" });
    setWaitingSetter(() => setWaiting); // <-- Ajout
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 🆕 Fonction pour ajouter rapidement une dépense depuis le formulaire moderne
  const ajouterDepenseRapide = (rubriqueNom, sousRubriqueNom) => {
    if (!montantDepense || isNaN(montantDepense) || Number(montantDepense) <= 0) {
      setErrorMessage('Le montant doit être un nombre positif.');
      return;
    }

    const nouvelleDepense = {
      id: Date.now(),
      nom: projectData.nom,
      description: description || '',
      chefChantier: projectData.chefChantier || projectData.responsableChantier || '',
      date: new Date().toISOString(),
      montant: Number(montantDepense),
      status: 'en_attente',
      createdBy: projectData.createdBy || 'user_id',
      rubrique: rubriqueNom,
      sousRubrique: sousRubriqueNom,
      fournisseur,
      justificatif: justificatif || null,
      justificatifFile,
      rubriques: [
        {
          rubrique: rubriqueNom,
          sousRubrique: sousRubriqueNom,
          montant: Number(montantDepense),
          fournisseur,
          justificatif: justificatif || null
        }
      ]
    };

    setDepenses([...depenses, nouvelleDepense]);
    setExpandedRubriques(prev => ({ ...prev, [rubriqueNom]: true }));
    
    // Reset des champs après ajout
    setMontantDepense('');
    setFournisseur('');
    setDescription('');
    setJustificatif(null);
    setJustificatifFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setMontantAffiche('');
    
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 1500);
  };

  const ajouterDepense = () => {
    setErrorMessage('');

    if (!rubrique || !sousRubrique || !montantDepense) {
      setErrorMessage('Veuillez remplir tous les champs obligatoires.');
      return;
    }

    if (isNaN(montantDepense) || Number(montantDepense) <= 0) {
      setErrorMessage('Le montant doit être un nombre positif.');
      return;
    }

    const nouvelleDepense = {
      id: Date.now(),
      nom: projectData.nom,
      description: description || '',
      chefChantier: projectData.chefChantier || projectData.responsableChantier || '',
      date: new Date().toISOString(),
      montant: Number(montantDepense),
      status: 'en_attente',
      createdBy: projectData.createdBy || 'user_id',
      rubrique,
      sousRubrique,
      fournisseur,
      justificatif: justificatif || null,
      justificatifFile,
      rubriques: [
        {
          rubrique,
          sousRubrique,
          montant: Number(montantDepense),
          fournisseur,
          justificatif: justificatif || null
        }
      ]
    };

    if (depenseAModifier) {
      setDepenses([...depenses, nouvelleDepense]);
      setDepenseAModifier(null);
    } else {
      setDepenses([...depenses, nouvelleDepense]);
    }

    setExpandedRubriques(prev => ({ ...prev, [rubrique]: true }));
    setRubrique('');
    setSousRubrique('');
    setMontantDepense('');
    setFournisseur('');
    setDescription('');
    setJustificatif(null);
    setJustificatifFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 1000);
  };

  const supprimerDepense = (id) => {
    setDepenses(depenses.filter(d => d.id !== id));
  };

  // 🆕 Optimisation de la soumission des dépenses (plus rapide)
 const soumettreDepenses = async () => {
  if (depenses.length === 0) {
    setErrorMessage('Aucune dépense à soumettre. Veuillez ajouter au moins une dépense.');
    return;
  }

  // Affiche le succès et ferme le formulaire AVANT l'envoi
  setShowSuccessMessage(true);
  setDepenses([]);
  setExpandedRubriques({});
  setShowModernForm(false);

  setTimeout(() => setShowSuccessMessage(false), 2000);

  try {
    setIsSubmitting(true);
    setErrorMessage('');

    // Envoi en parallèle pour aller plus vite
    const promises = depenses.map(depense => 
      createDepense({ ...depense, justificatifFile: depense.justificatifFile })
    );
    await Promise.all(promises);

    // Plus besoin de setShowSuccessMessage ici, déjà fait plus haut
  } catch (error) {
    setErrorMessage('Erreur lors de la soumission des dépenses: ' + error.message);
    console.error('Erreur soumission:', error);
  } finally {
    setIsSubmitting(false);
  }
};

  const handleLogout = () => {
    // Clear all project-related state
    setProjectData(null);
    setDepenses([]);
    setExpandedRubriques({});
    setCurrentView('dashboard');
    setCurrentComponent(null);
    
    logout();
    navigate('/login', { replace: true });
  };

  const depensesGroupees = depenses.reduce((groupes, depense) => {
    if (!groupes[depense.rubrique]) {
      groupes[depense.rubrique] = [];
    }
    groupes[depense.rubrique].push(depense);
    return groupes;
  }, {});

  const toggleRubrique = (rubrique) => {
    setExpandedRubriques(prev => ({
      ...prev,
      [rubrique]: !prev[rubrique]
    }));
  };

  const totalDepense = depenses.reduce((sum, d) => sum + d.montant, 0);

  const totalParRubrique = (rubriqueName) => {
    return (depensesGroupees[rubriqueName] || []).reduce((sum, d) => sum + d.montant, 0);
  };

  useEffect(() => {
    if (projectData) {
      if (Array.isArray(projectData.chefsChantier)) {
        setChefsDisponibles(projectData.chefsChantier);
        setChefChantierNom(projectData.chefsChantier[0] || '');
      } else if (projectData.responsableChantier) {
        setChefsDisponibles([projectData.responsableChantier]);
        setChefChantierNom(projectData.responsableChantier);
      }
    }
  }, [projectData]);

  if (!projectData) {
    return <ProjectIdValidator onValidProject={handleValidProject} />;
  }

  return (
    <div className="bg-gray-50 text-gray-900 min-h-screen flex flex-col animate-fade-in">
      <div className="fixed top-0 left-0 right-0 z-50 flex bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 h-14 items-center justify-between">
        <div className="flex items-center space-x-4 min-w-0">
          <img src="dywanes-logo.png" alt="Dywane Logo" className="h-10 w-auto" />
        </div>
        <div className="flex-1 max-w-lg mx-4">
          <div className="relative text-gray-400 focus-within:text-gray-600">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input type="search" placeholder="Rechercher une dépense..." className="block w-full rounded-md border border-gray-300 bg-gray-100 py-1.5 pl-10 pr-3 text-sm placeholder-gray-400 focus:border-gray-400 focus:ring-0 focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center space-x-2 min-w-0">
          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-600 text-sm select-none">
            <FontAwesomeIcon icon={faUser} />
          </div>
          <span className="text-sm truncate min-w-0">{projectData.responsableChantier || 'N/A'}</span>
          <button onClick={() => setShowLogoutConfirm(true)} className="text-gray-500 hover:text-gray-700 focus:outline-none">
            <FontAwesomeIcon icon={faSignOutAlt} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <nav className="fixed top-14 left-0 z-40 h-[calc(100vh-3.5rem)] bg-gray-900 w-56 flex-shrink-0 px-4 py-6 flex flex-col space-y-6 text-gray-300 text-sm select-none animate-slide-in-left">
          <button 
            className="flex items-center space-x-2 w-full rounded-md bg-blue-800 px-4 py-2 text-white font-semibold"
            onClick={() => {
              setCurrentView('dashboard');
            }}
          >
            <FontAwesomeIcon icon={faHome} /><span>Tableau de bord</span>
          </button>
          <button
            onClick={() => {
              setCurrentView('notifications');
              setCurrentComponent(null);
            }}
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 ${
              currentView === "notifications"
                ? "bg-blue-800 text-white font-semibold"
                : "hover:text-white"
            }`}
          >
            <span className="relative">
              <FontAwesomeIcon icon={faBell} />
              {notifCount > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5 shadow"
                  style={{ minWidth: 18, textAlign: "center" }}
                >
                  {notifCount}
                </span>
              )}
            </span>
            <span>Notifications</span>
          </button>
          <button
            onClick={() => {
              setCurrentView('etat');
              setCurrentComponent(null);
            }}
            className={`flex items-center space-x-2 w-full rounded-md px-4 py-2 ${
              currentView === "etat"
                ? "bg-blue-800 text-white font-semibold"
                : "hover:text-white"
            }`}
          >
            <span>📈</span>
            <span>État</span>
          </button>
          <div className="flex-grow" />
          <div className="border-t border-gray-700 pt-6">
            <a href="#" className="flex items-center space-x-2 hover:text-white">
              <FontAwesomeIcon icon={faCog} /><span>Paramètres</span>
            </a>
            <button onClick={() => setShowLogoutConfirm(true)} className="flex items-center space-x-2 mt-4 hover:text-white w-full text-left">
              <FontAwesomeIcon icon={faSignOutAlt} /><span>Déconnexion</span>
            </button>
          </div>
        </nav>

        <main className="flex-1 overflow-auto">
          {currentView === 'dashboard' ? (
            <div className="p-6 pl-60 pt-20 animate-slide-in-right">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-lg font-bold text-gray-900">
                  Projet: {projectData.nom || projectData.name || 'N/A'} 🏗️
                </h1>
                <div className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                  ID: {projectData.id}
                </div>
              </div>

              {showSuccessMessage && (
                <div className="mb-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow animate-fade-in">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <p>Dépense ajoutée avec succès!</p>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className="mb-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow animate-fade-in">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p>{errorMessage}</p>
                  </div>
                </div>
              )}

              {/* 🆕 NOUVEAU DESIGN MODERNE - Interface style Contrôleur de Gestion */}
              <div className="bg-white shadow-2xl rounded-3xl p-8 max-w-6xl mx-auto mt-10">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3 tracking-wide">
                    <FaFolderOpen className="text-blue-600 text-2xl" />
                    Détails du projet
                  </h2>
                  <button
                    onClick={() => setShowModernForm(!showModernForm)}
                    className={`px-6 py-3 rounded-xl shadow-lg font-bold text-base transition-all duration-200 ${
                      showModernForm 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : 'bg-gradient-to-r from-green-600 to-green-500 text-white hover:from-green-700 hover:to-green-600'
                    }`}
                  >
                    {showModernForm ? '❌ Fermer' : '💰 Ajouter des dépenses'}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700 text-base mb-8">
                  <div className="flex items-center gap-3">
                    <FaUserTie className="text-green-500 text-lg" />
                    <span><strong>Responsable :</strong> {projectData.responsableChantier || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaCalendarAlt className="text-purple-500 text-lg" />
                    <span><strong>Date de début :</strong> 
                      {projectData.date
                        ? new Date(projectData.date).toLocaleDateString()
                        : 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FaMapMarkedAlt className="text-red-500 text-lg" />
                    <span><strong>Nom du projet :</strong> {projectData.nom || projectData.name || 'N/A'}</span>
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <strong>Description :</strong>
                    <p className="mt-1 text-gray-600 leading-relaxed">{projectData.description || 'Aucune description fournie.'}</p>
                  </div>
                </div>

                {/* 🆕 FORMULAIRE MODERNE style CG */}
                {showModernForm && (
                  <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl p-6 mb-8 animate-fade-in">
                    <h3 className="text-2xl font-bold text-blue-800 mb-6">💰 Saisie rapide des dépenses</h3>
                    
                    {/* Champs communs */}
                    <div className="bg-white border border-blue-200 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-blue-700 mb-4">📋 Informations communes</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={montantAffiche}
                          onChange={e => {
                            // On enlève tout sauf les chiffres
                            const value = e.target.value.replace(/\D/g, '');
                            setMontantDepense(value);
                            // Formatage avec séparateur d'espaces
                            setMontantAffiche(value.replace(/\B(?=(\d{3})+(?!\d))/g, ' '));
                          }}
                          placeholder="Montant (FCFA) *"
                          className="w-full rounded-lg border border-blue-300 px-4 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                        <input
                          type="text"
                          value={fournisseur}
                          onChange={(e) => setFournisseur(e.target.value.toUpperCase())}
                          placeholder="FOURNISSEUR (OPTIONNEL)"
                          className="w-full rounded-lg border border-blue-300 px-4 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 uppercase"
                        />
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          className="w-full rounded-lg border border-blue-300 px-4 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        />
                      </div>
                      <textarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Description de la dépense (optionnel)"
                        className="w-full rounded-lg border border-blue-300 px-4 py-2 text-sm mt-4 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 resize-y"
                        rows={2}
                      />
                    </div>

                    {/* 🆕 TABLEAU MODERNE des rubriques */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-gray-800">🔧 Sélectionnez la rubrique et sous-rubrique</h4>
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Dépenses ajoutées :</div>
                          <div className="text-xl font-bold text-blue-700">{depenses.length}</div>
                        </div>
                      </div>
                      
                      <div className="overflow-x-auto max-h-96 border border-gray-300 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-gradient-to-r from-blue-600 to-blue-500 text-white sticky top-0">
                            <tr>
                              <th className="p-3 text-left font-semibold border-r border-blue-400">Rubrique</th>
                              <th className="p-3 text-left font-semibold border-r border-blue-400">Sous-rubrique</th>
                              <th className="p-3 text-center font-semibold">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(rubriques).map(([rubrique, sousRubriques], rubriqueIndex) => (
                              <React.Fragment key={rubrique}>
                                {/* Ligne de rubrique principale */}
                                <tr className={`${rubriqueIndex % 2 === 0 ? 'bg-blue-50' : 'bg-green-50'} border-t-2 border-gray-300`}>
                                  <td className="p-3 font-bold text-blue-900 border-r border-gray-200" rowSpan={sousRubriques.length + 1}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-lg">
                                        {rubrique.includes('Terrain') ? '🏗️' :
                                         rubrique.includes('Matériaux') ? '🧱' :
                                         rubrique.includes('Électricité') ? '⚡' :
                                         rubrique.includes('Plomberie') ? '🚰' :
                                         rubrique.includes('Architecture') ? '📐' :
                                         rubrique.includes('Aluminium') ? '🔩' :
                                         rubrique.includes('Carrelage') ? '🏺' :
                                         rubrique.includes('Peinture') ? '🎨' :
                                         rubrique.includes('Sécurité') ? '🔥' :
                                         '📦'}
                                      </span>
                                      <span className="text-xs leading-tight">{rubrique}</span>
                                    </div>
                                  </td>
                                  <td colSpan="2" className="p-2 text-center text-xs text-gray-500 italic">
                                    Sous-rubriques disponibles : {sousRubriques.length}
                                  </td>
                                </tr>
                                
                                {/* Lignes des sous-rubriques */}
                                {sousRubriques.map((sousRubrique, sousIndex) => (
                                  <tr key={sousRubrique} className="hover:bg-yellow-50 transition-colors border-b border-gray-100">
                                    <td className="p-3 pl-6 text-gray-700 border-r border-gray-200">
                                      <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                                        <span className="text-xs">{sousRubrique}</span>
                                      </div>
                                    </td>
                                    <td className="p-2 text-center">
                                      <button
                                        onClick={() => {
                                          if (!montantDepense || isNaN(montantDepense) || Number(montantDepense) <= 0) {
                                            setErrorMessage('Veuillez saisir un montant valide avant d\'ajouter la dépense.');
                                            return;
                                          }
                                          ajouterDepenseRapide(rubrique, sousRubrique);
                                        }}
                                        className="bg-gradient-to-r from-green-600 to-green-500 text-white px-3 py-1 rounded-lg text-xs font-semibold hover:from-green-700 hover:to-green-600 transition-all duration-200 shadow"
                                      >
                                        ➕ Ajouter
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-4 text-xs text-gray-600 bg-blue-50 p-3 rounded">
                        💡 <strong>Instructions rapides :</strong>
                        <ul className="mt-1 ml-4 space-y-1">
                          <li>• Remplissez le montant et les infos communes en haut</li>
                          <li>• Cliquez sur "Ajouter" pour la ligne souhaitée</li>
                          <li>• La dépense s'ajoute automatiquement au tableau récapitulatif</li>
                          <li>• Soumettez toutes vos dépenses en une fois</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ancien formulaire classique (optionnel) */}
                {showForm && !showModernForm && (
                  <div className="mt-8 space-y-5 bg-gray-50 rounded-2xl p-6 border border-gray-200 animate-fade-in">
                    <h3 className="text-xl font-semibold text-gray-800">
                      {depenseAModifier ? "Modifier la dépense mise en attente" : "Ajouter une dépense"}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <select
                        value={rubrique}
                        onChange={e => { setRubrique(e.target.value); setSousRubrique(''); }}
                        className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Rubrique</option>
                        {Object.keys(rubriques).map(key => (
                          <option key={key} value={key}>{key}</option>
                        ))}
                      </select>
                      <select
                        value={sousRubrique}
                        onChange={e => setSousRubrique(e.target.value)}
                        disabled={!rubrique}
                        className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Sous-rubrique</option>
                        {rubrique && rubriques[rubrique].map(sub => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="number"
                      value={montantDepense}
                      onChange={(e) => setMontantDepense(e.target.value)}
                      placeholder="Montant dépensé"
                      className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <input
                      type="text"
                      value={fournisseur}
                      onChange={(e) => setFournisseur(e.target.value.toUpperCase())}
                      placeholder="FOURNISSEUR (OPTIONNEL)"
                      className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 uppercase"
                    />
                    <textarea
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Petite description sur cette dépense (optionnel)"
                      className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-y"
                      rows={3}
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="w-full rounded-lg border px-4 py-2 text-sm"
                    />

                    <div className="flex gap-4">
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          if (!rubrique || !sousRubrique || !montantDepense) {
                            setErrorMessage('Veuillez remplir tous les champs obligatoires.');
                            return;
                          }
                          if (isNaN(montantDepense) || Number(montantDepense) <= 0) {
                            setErrorMessage('Le montant doit être un nombre positif.');
                            return;
                          }
                          if (depenseAModifier) {
                            try {
                              // 1. Créer une nouvelle demande dans `demandesDepenses`
                              const nouvelleDemandeData = {
                                nom: projectData.nom,
                                nomProjet: projectData.nom,
                                description: description,
                                chefChantier: projectData.responsableChantier,
                                date: new Date().toISOString(),
                                montant: Number(montantDepense),
                                status: 'en_attente',
                                createdBy: pb.authStore.model.id,
                                rubrique: rubrique,
                                sousRubrique: sousRubrique,
                                fournisseur: fournisseur,
                                justificatifFile: justificatifFile,
                                // Le service createDepense s'attend à un champ 'rubriques'
                                rubriques: [
                                  {
                                    rubrique: rubrique,
                                    sousRubrique: sousRubrique,
                                    montant: Number(montantDepense),
                                    fournisseur: fournisseur,
                                  },
                                ],
                              };
                              // La fonction createDepense est conçue pour cela
                              await createDepense(nouvelleDemandeData);

                              // 2. Mettre à jour l'ancienne dépense dans `depensemea` comme "traité"
                              const updateDataMEA = {
                                status: 'traité',
                                dateTraitement: new Date().toISOString(),
                                traitePar: 'chef_chantier',
                                renvoyePar: 'chef_chantier',
                                dateRenvoi: new Date().toISOString(),
                              };
                              await updateDepenseMEA(depenseAModifier.id, updateDataMEA, depenseAModifier.sourceCollection);
                              
                              // 3. Nettoyage de l'interface
                              if (typeof waitingSetter === "function") {
                                // Retire la notif de la liste d'attente dans l'UI
                                waitingSetter(prev => prev.filter(n => n.id !== depenseAModifier.id));
                              }

                              setShowForm(false);
                              setDepenseAModifier(null);
                              setRubrique('');
                              setSousRubrique('');
                              setMontantDepense('');
                              setFournisseur('');
                              setDescription('');
                              setJustificatif(null);
                              setJustificatifFile(null);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                              setMontantAffiche('');
                              setErrorMessage('');
                              
                              setShowSuccessMessage(true);
                              setTimeout(() => setShowSuccessMessage(false), 3000);
                              setErrorMessage('Dépense renvoyée pour une nouvelle validation avec succès !');
                              setTimeout(() => setErrorMessage(''), 3000);

                            } catch (error) {
                              console.error('Erreur lors du renvoi de la dépense:', error);
                              setErrorMessage("Erreur lors du renvoi : " + (error?.message || error));
                            }
                          } else {
                            ajouterDepense();
                          }
                        }}
                        className={`${
                          depenseAModifier
                            ? "bg-blue-700 hover:bg-blue-800 shadow-lg"
                            : "bg-green-600 hover:bg-green-700"
                        } text-white px-5 py-2.5 rounded-xl font-semibold transition duration-200 flex items-center gap-2`}
                      >
                        {depenseAModifier ? (
                          <>
                            <FontAwesomeIcon icon={faPaperPlane} />
                            Renvoyer à Nouveau
                          </>
                        ) : (
                          "Ajouter dépense"
                        )}
                      </button>
                      {depenseAModifier && (
                        <button
                          type="button"
                          onClick={() => {
                            setDepenseAModifier(null);
                            setShowForm(false);
                            setRubrique('');
                            setSousRubrique('');
                            setMontantDepense('');
                            setFournisseur('');
                            setDescription('');
                            setJustificatif(null);
                            setJustificatifFile(null);
                            setErrorMessage('');
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="bg-gray-200 text-gray-700 px-5 py-2.5 rounded-xl shadow hover:bg-gray-300 transition"
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* 🆕 TABLEAU RÉCAPITULATIF OPTIMISÉ */}
              {showModernForm && depenses.length > 0 && (
                <div className="mt-6 bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-6 py-4 font-semibold text-lg shadow flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <span>📋 Récapitulatif des dépenses</span>
                      <span className="bg-white text-blue-600 px-3 py-1 rounded-full text-sm font-bold">
                        {depenses.length} dépense{depenses.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <button 
                      onClick={soumettreDepenses}
                      disabled={isSubmitting}
                      className={`flex items-center bg-white text-blue-600 px-6 py-2 rounded-xl text-sm font-bold transition-all duration-200 shadow-lg ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-50 hover:scale-105'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                          🚀 Soumettre maintenant
                        </>
                      )}
                    </button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                        <tr>
                          <th className="p-4 font-semibold">Rubrique / Sous-rubrique</th>
                          <th className="p-4 text-center font-semibold">Montant</th>
                          <th className="p-4 text-center font-semibold">Fournisseur</th>
                          <th className="p-4 text-center font-semibold">Description</th>
                          <th className="p-4 text-center font-semibold">Justificatif</th>
                          <th className="p-4 text-center font-semibold">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(depensesGroupees).map(rubriqueNom => (
                          <React.Fragment key={rubriqueNom}>
                            <tr className="bg-blue-50 hover:bg-blue-100 cursor-pointer border-b-2 border-blue-200" onClick={() => toggleRubrique(rubriqueNom)}>
                              <td className="p-4 font-bold text-blue-800 flex items-center">
                                <FontAwesomeIcon 
                                  icon={expandedRubriques[rubriqueNom] ? faChevronDown : faChevronRight} 
                                  className="mr-3 text-blue-600" 
                                />
                                <span className="text-lg">
                                  {rubriqueNom.includes('Terrain') ? '🏗️' :
                                   rubriqueNom.includes('Matériaux') ? '🧱' :
                                   rubriqueNom.includes('Électricité') ? '⚡' :
                                   rubriqueNom.includes('Plomberie') ? '🚰' :
                                   rubriqueNom.includes('Architecture') ? '📐' :
                                   '📦'}
                                </span>
                                <span className="ml-2">{rubriqueNom}</span>
                              </td>
                              <td className="p-4 text-center font-bold text-blue-800">
                                {totalParRubrique(rubriqueNom).toLocaleString()} FCFA
                              </td>
                              <td className="p-4 text-center text-gray-500">—</td>
                              <td className="p-4 text-center text-gray-500">—</td>
                              <td className="p-4 text-center text-gray-500">—</td>
                              <td className="p-4 text-center">
                                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-bold">
                                  {depensesGroupees[rubriqueNom].length} item{depensesGroupees[rubriqueNom].length > 1 ? 's' : ''}
                                </span>
                              </td>
                            </tr>
                            {expandedRubriques[rubriqueNom] && depensesGroupees[rubriqueNom].map((d) => (
                              <tr key={d.id} className="border-b hover:bg-gray-50 transition-colors">
                                <td className="p-4 pl-12 text-sm text-gray-700">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                                    {d.sousRubrique}
                                  </div>
                                </td>
                                <td className="p-4 text-center font-semibold text-gray-900">
                                  {d.montant.toLocaleString()} FCFA
                                </td>
                                <td className="p-4 text-center text-gray-600">{d.fournisseur || '—'}</td>
                                <td className="p-4 text-center max-w-xs">
                                  {d.description && d.description.length > 40 ? (
                                    <span>
                                      <span className="truncate inline-block max-w-[150px] align-middle" title={d.description}>
                                        {d.description.slice(0, 40) + '...'}
                                      </span>
                                      <button
                                        type="button"
                                        className="ml-2 text-blue-600 underline text-xs font-semibold"
                                        onClick={() => alert(d.description)}
                                        tabIndex={0}
                                      >
                                        Lire
                                      </button>
                                    </span>
                                  ) : (
                                    d.description || '—'
                                  )}
                                </td>
                                <td className="p-4 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                    d.justificatif ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {d.justificatif ? "✓ Oui" : "✗ Non"}
                                  </span>
                                </td>
                                <td className="p-4 text-center">
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation(); 
                                      supprimerDepense(d.id);
                                    }} 
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors text-xs font-semibold"
                                  >
                                    🗑️ Supprimer
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                    <div className="bg-gradient-to-r from-green-600 to-green-500 text-white px-6 py-4 text-right font-bold text-lg">
                      💰 Total général: {totalDepense.toLocaleString()} FCFA
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : currentView === 'etat' ? (
            <div className="p-6 pl-60 pt-20 animate-fade-in">
              <div className="mb-6 flex items-center gap-4">
                <label className="font-semibold text-gray-700">Chef de chantier :</label>
                <select
                  value={chefChantierNom}
                  onChange={e => setChefChantierNom(e.target.value)}
                  className="bg-blue-600 text-white rounded-full px-4 py-2 font-bold text-lg shadow"
                >
                  {chefsDisponibles.map(chef => (
                    <option key={chef} value={chef}>{chef}</option>
                  ))}
                </select>
              </div>
              <FiltreDepensesChef
                projectId={projectData.id}
                chefChantierNom={chefChantierNom}
                nomProjet={projectData.nom || projectData.name}
              />
            </div>
          ) : currentView === 'notifications' ? (
            <NotificationsView
              projectId={projectData.id}
              setNotifCount={setNotifCount}
              onEditDepense={(notif, setWaiting) => handleEditDepenseFromNotif(notif, setWaiting)}
            />
          ) : currentComponent}
        </main>
      </div>

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center">
          <div className="bg-white p-7 rounded-lg shadow-xl text-center">
            <h2 className="text-lg font-bold mb-4">Confirmation</h2>
            <p className="mb-6">Êtes-vous sûr de vouloir vous déconnecter ?</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-5 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition"
              >
                Non
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setShowLogoutConfirm(false);
                }}
                className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
              >
                Oui
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant NotificationsView (inchangé)
const NotificationsView = ({ projectId, setNotifCount, onEditDepense }) => {
  const [waiting, setWaiting] = useState([]);
  const [validated, setValidated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [selectedValid, setSelectedValid] = useState(null);
  const [tab, setTab] = useState('en_attente');
  const [nomProjet, setNomProjet] = useState('');
  const [newWaitingIds, setNewWaitingIds] = useState(new Set());
  const [newValidatedIds, setNewValidatedIds] = useState(new Set());
  const newWaitingIdsRef = useRef(newWaitingIds);
  const newValidatedIdsRef = useRef(newValidatedIds);

  useEffect(() => {
    newWaitingIdsRef.current = newWaitingIds;
    newValidatedIdsRef.current = newValidatedIds;
  }, [newWaitingIds, newValidatedIds]);

  const getSeenWaitingKey = () => `seenWaitingNotifications_chef_${projectId}`;
  const getSeenValidatedKey = () => `seenValidatedNotifications_chef_${projectId}`;

  const getSeenIds = (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? new Set(JSON.parse(item)) : new Set();
    } catch (e) {
      return new Set();
    }
  };

  const addSeenIds = (key, idsToAdd) => {
    if (!idsToAdd || idsToAdd.size === 0) return;
    const seenIds = getSeenIds(key);
    idsToAdd.forEach(id => seenIds.add(id));
    localStorage.setItem(key, JSON.stringify(Array.from(seenIds)));
  };

  useEffect(() => {
    return () => {
      addSeenIds(getSeenWaitingKey(), newWaitingIdsRef.current);
      addSeenIds(getSeenValidatedKey(), newValidatedIdsRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const getHiddenWaiting = () => JSON.parse(localStorage.getItem("hiddenWaitingChef") || "[]");
  const getHiddenValidated = () => JSON.parse(localStorage.getItem("hiddenValidatedChef") || "[]");

  useEffect(() => {
    const fetchNomProjet = async () => {
      try {
        const projet = await pb.collection('projects').getOne(projectId);
        setNomProjet(projet.nom || projet.name || '');
      } catch {
        setNomProjet('');
      }
    };
    fetchNomProjet();
  }, [projectId]);

  useEffect(() => {
    if (!nomProjet) return;

    // --- 1. Initial Data Loading ---
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [waitingData, validatedData] = await Promise.all([
          pb.collection('depensemea').getFullList({
            filter: `nomProjet = "${nomProjet}" && meaPar = "Responsable Technique" && status != "traité"`,
            sort: '-created',
          }),
          pb.collection('depensesValideesDG').getFullList({
            filter: `nomProjet = "${nomProjet}"`,
            sort: '-created',
          }),
        ]);
        
        const seenWaiting = getSeenIds(getSeenWaitingKey());
        const seenValidated = getSeenIds(getSeenValidatedKey());

        const finalWaitingData = waitingData.filter(d => d.meaPar === "Responsable Technique" && d.status !== "traité");
        const finalValidatedData = validatedData.filter(d => d.nomProjet === nomProjet);

        // Filter out validated notifications that are hidden in localStorage
        const hiddenValidated = getHiddenValidated();
        const filteredValidatedData = finalValidatedData.filter(d => !hiddenValidated.includes(d.id));

        setNewWaitingIds(new Set(finalWaitingData.filter(d => !seenWaiting.has(d.id)).map(d => d.id)));
        setNewValidatedIds(new Set(filteredValidatedData.filter(d => !seenValidated.has(d.id)).map(d => d.id)));
        
        setWaiting(finalWaitingData);
        setValidated(filteredValidatedData);
        
        // Update notification count in parent component
        if (setNotifCount) {
          setNotifCount(finalWaitingData.length + filteredValidatedData.length);
        }
      } catch (error) {
        console.error("Erreur chargement initial des notifications:", error);
        setWaiting([]);
        setValidated([]);
        if (setNotifCount) setNotifCount(0);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // --- 2. Real-time Subscription Handlers ---
    const handleWaitingUpdate = (e) => {
      const { action, record } = e;
      
      if (record.nomProjet !== nomProjet) {
        return; // Ignore notifications for other projects
      }

      // A notification is relevant if it's for the RT and not yet treated.
      const isRelevant = record.meaPar === "Responsable Technique" && record.status !== "traité";

      setWaiting(prev => {
        const index = prev.findIndex(item => item.id === record.id);
        const exists = index !== -1;

        if (action === 'delete') {
          const newList = exists ? prev.filter(item => item.id !== record.id) : prev;
          if (setNotifCount) setNotifCount(newList.length + validated.length);
          return newList;
        }

        if (isRelevant) {
          // If relevant, add or update it in the list.
          if (exists) {
            const updatedList = [...prev];
            updatedList[index] = record;
            if (setNotifCount) setNotifCount(updatedList.length + validated.length);
            return updatedList;
          } else {
            const newList = [record, ...prev];
            if (setNotifCount) setNotifCount(newList.length + validated.length);
            return newList;
          }
        } else {
          // If not relevant, it should be removed from the list if it exists.
          const newList = exists ? prev.filter(item => item.id !== record.id) : prev;
          if (setNotifCount) setNotifCount(newList.length + validated.length);
          return newList;
        }
      });
    };

    const handleValidatedUpdate = (e) => {
      const { action, record } = e;
      if (record.nomProjet !== nomProjet) return;

      setValidated(prev => {
        const index = prev.findIndex(item => item.id === record.id);
        const exists = index !== -1;

        if (action === 'delete') {
          const newList = exists ? prev.filter(item => item.id !== record.id) : prev;
          if (setNotifCount) setNotifCount(waiting.length + newList.length);
          return newList;
        }
        
        if (exists) { // Mise à jour
          const updatedList = [...prev];
          updatedList[index] = record;
          if (setNotifCount) setNotifCount(waiting.length + updatedList.length);
          return updatedList.sort((a, b) => new Date(b.dateValidationDG || b.created) - new Date(a.dateValidationDG || a.created));
        }
        
        // Création
        const newList = [record, ...prev].sort((a, b) => new Date(b.dateValidationDG || b.created) - new Date(a.dateValidationDG || a.created));
        if (setNotifCount) setNotifCount(waiting.length + newList.length);
        return newList;
      });
    };

    // --- 3. Subscribing and Cleaning up ---
    let unsubWaiting, unsubValidated;
    const subscribe = async () => {
        if (!pb.authStore.isValid) {
          console.debug('Subscriptions skipped: not authenticated');
          return;
        }

        try {
            unsubWaiting = await pb.collection('depensemea').subscribe('*', handleWaitingUpdate);
            unsubValidated = await pb.collection('depensesValideesDG').subscribe('*', handleValidatedUpdate);
        } catch (err) {
            console.debug("Subscriptions skipped:", err?.message);
        }
    };

    subscribe();

    return () => {
      if (unsubWaiting) unsubWaiting();
      if (unsubValidated) unsubValidated();
    };
  }, [nomProjet, projectId, setNotifCount]);

  useEffect(() => {
    if (setNotifCount) setNotifCount(newWaitingIds.size + newValidatedIds.size);
  }, [newWaitingIds, newValidatedIds, setNotifCount]);

  const handleDelete = (notif) => {
    const hidden = getHiddenWaiting();
    const notifKey = `${notif.sourceCollection}-${notif.id}`;
    if (!hidden.includes(notifKey)) {
      hidden.push(notifKey);
      localStorage.setItem("hiddenWaitingChef", JSON.stringify(hidden));
    }
    setWaiting(prev => prev.filter(n => !(n.id === notif.id && n.sourceCollection === notif.sourceCollection)));
    if (selectedNotif && selectedNotif.id === notif.id && selectedNotif.sourceCollection === notif.sourceCollection) {
      setSelectedNotif(null);
    }
  };

  const handleDeleteValid = (notif) => {
    const hidden = getHiddenValidated();
    if (!hidden.includes(notif.id)) {
      hidden.push(notif.id);
      localStorage.setItem("hiddenValidatedChef", JSON.stringify(hidden));
    }
    setValidated(prev => prev.filter(n => n.id !== notif.id));
    if (selectedValid && selectedValid.id === notif.id) setSelectedValid(null);
  };

  const getSourceInfo = (sourceCollection) => {
    switch (sourceCollection) {
      case 'depensemeaRT':
        return { icon: '🔧', name: 'Responsable Technique', color: 'text-blue-600' };
      case 'depensemeaCG':
        return { icon: '💼', name: 'Contrôleur de Gestion', color: 'text-purple-600' };
      case 'depensemeaDG':
        return { icon: '👔', name: 'Directeur Général', color: 'text-green-600' };
      default:
        return { icon: '⏸️', name: 'Mise en attente', color: 'text-yellow-600' };
    }
  };

  const handleRenvoyerDepense = async (notif) => {
    try {
      const updateData = {
        status: 'traité',
        dateRenvoi: new Date().toISOString(),
        renvoyePar: 'chef_chantier',
        dateTraitement: new Date().toISOString(),
        traitePar: 'chef_chantier'
      };

      await updateDepenseMEA(
        notif.id,
        updateData,
        notif.sourceCollection
      );

      setWaiting(prev => prev.filter(n => !(n.id === notif.id && n.sourceCollection === notif.sourceCollection)));
      
      alert("Dépense marquée comme traitée avec succès !");
      
    } catch (error) {
      console.error('Erreur lors du traitement:', error);
      alert("Erreur lors du traitement : " + (error?.message || error));
    }
  };

  const renderDetailsModal = (notif, type = "waiting") => {
    if (!notif) return null;
    const rubriqueAffichee =
      notif.rubrique ||
      (notif.rubriques && notif.rubriques.length > 0 && notif.rubriques[0].rubrique) ||
      "-";
    const sousRubriqueAffichee =
      notif.sousRubrique ||
      (notif.rubriques && notif.rubriques.length > 0 && notif.rubriques[0].sousRubrique) ||
      "-";

    const sourceInfo = type === "waiting" ? getSourceInfo(notif.sourceCollection) : null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 border-4 border-yellow-200 animate-fade-in flex flex-col max-h-[90vh]">
          <div className="px-6 py-4 border-b border-yellow-100 flex justify-between items-center rounded-t-2xl bg-gradient-to-r from-yellow-50 to-yellow-100">
            <h3 className="text-lg font-bold text-yellow-800 flex items-center gap-2">
              <span className={type === "waiting" ? sourceInfo?.color : "text-green-500"}>
                {type === "waiting" ? sourceInfo?.icon : <FontAwesomeIcon icon={faBalanceScale} />}
              </span>
              Détails de la dépense {type === "waiting" ? "mise en attente" : "validée"}
            </h3>
            <button
              onClick={() => type === "waiting" ? setSelectedNotif(null) : setSelectedValid(null)}
              className="text-gray-400 hover:text-gray-600 transition-colors text-2xl"
            >
              ✕
            </button>
          </div>
          <div className="px-6 py-6 bg-gradient-to-br from-yellow-50 to-white flex-1 overflow-y-auto">
            <div className="space-y-4">
              {type === "waiting" && sourceInfo && (
                <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                  <span className="font-semibold text-yellow-700">Mise en attente par :</span>
                  <span className={`flex items-center gap-1 ${sourceInfo.color} font-semibold`}>
                    <span>{sourceInfo.icon}</span>
                    {sourceInfo.name}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="font-semibold text-yellow-700">Rubrique :</span>
                <span>{rubriqueAffichee}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-yellow-700">Sous-rubrique :</span>
                <span>{sousRubriqueAffichee}</span>
              </div>
              {type === "waiting" && (
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-yellow-700">Motif de la mise en attente :</span>
                  <span className="text-yellow-700">{notif.motif || "-"}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="font-semibold text-yellow-700">Montant :</span>
                <span className="font-mono text-yellow-900">{notif.montant?.toLocaleString()} FCFA</span>
              </div>
              <div>
                <span className="font-semibold text-yellow-700">Description :</span>
                <div className="mt-1 bg-yellow-50 rounded p-2 text-gray-700 whitespace-pre-wrap">{notif.description || "-"}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-yellow-700">Date :</span>
                <span>
                  {type === "waiting"
                    ? (notif.datemea
                        ? new Date(notif.datemea).toLocaleString()
                        : notif.created
                          ? new Date(notif.created).toLocaleString()
                          : "-")
                    : ((notif.dateValidationDG || notif.dateValidationCG || notif.dateValidationRT || notif.created)
                        ? new Date(notif.dateValidationDG || notif.dateValidationCG || notif.dateValidationRT || notif.created).toLocaleString()
                        : "-")
                  }
                </span>
              </div>
              {notif.fournisseur && (
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-yellow-700">Fournisseur :</span>
                  <span>{notif.fournisseur}</span>
                </div>
              )}
            </div>
          </div>
          <div className="px-6 py-4 bg-yellow-50 flex justify-end rounded-b-2xl">
            <button
              onClick={() => type === "waiting" ? setSelectedNotif(null) : setSelectedValid(null)}
              className="px-5 py-2 bg-yellow-200 text-yellow-900 rounded-lg font-semibold hover:bg-yellow-300 transition-colors shadow"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 min-h-[calc(100vh-3.5rem)] bg-gray-50 animate-fade-in pl-56 pt-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-yellow-800 flex items-center gap-2">
            <span>⏸️</span> Centre de notifications
          </h2>
          <p className="mt-2 text-gray-600">Consultez vos notifications de dépenses mises en attente et validées</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <button
              onClick={() => setTab("en_attente")}
              className={`flex-1 px-6 py-3 rounded-lg flex items-center justify-center gap-2 ${
                tab === "en_attente"
                  ? "bg-yellow-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <span className="text-lg">⏸️</span>
              <span className="font-semibold">Dépenses mises en attente</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                tab === "en_attente"
                  ? "bg-white text-yellow-600"
                  : "bg-yellow-200 text-yellow-700"
              }`}>
                {waiting.length}
              </span>
            </button>
            <button
              onClick={() => setTab("validées")}
              className={`flex-1 px-6 py-3 rounded-lg flex items-center justify-center gap-2 ${
                tab === "validées"
                  ? "bg-green-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <FontAwesomeIcon icon={faBalanceScale} />
              <span className="font-semibold">Dépenses validées</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  tab === "validées" ? "bg-white text-green-600"
                  : "bg-green-200 text-green-700"
              }`}>
                {validated.length}
              </span>
            </button>
          </div>
          <div className="mt-6">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
              </div>
            ) : tab === "en_attente" ? (
              waiting.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <span className="text-yellow-400 text-4xl mb-3">⏸️</span>
                  <p className="text-gray-600">Aucune dépense mise en attente</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {waiting.map((notif) => {
                    const sourceInfo = getSourceInfo(notif.sourceCollection);
                    return (
                      <div
                        key={`${notif.sourceCollection}-${notif.id}`}
                        className="relative bg-gradient-to-br from-yellow-50 to-white rounded-xl shadow-lg p-5 border-l-8 border-yellow-500 animate-fade-in transition-transform hover:scale-105 hover:shadow-2xl"
                      >
                        {newWaitingIds.has(notif.id) && (
                          <span className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">1</span>
                        )}
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xl ${sourceInfo.color}`}>{sourceInfo.icon}</span>
                          <span className="font-semibold text-sm">Mise en attente par {sourceInfo.name}</span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2 italic">
                          {notif.datemea
                            ? new Date(notif.datemea).toLocaleString()
                            : notif.created
                              ? new Date(notif.created).toLocaleString()
                              : "-"}
                        </div>
                        <div className="bg-yellow-100 p-3 rounded-lg mb-3 border border-yellow-200">
                          <div className="font-bold text-sm text-yellow-700 truncate">{notif.rubrique}</div>
                          <div className="text-sm text-gray-600 truncate">{notif.motif || "-"}</div>
                          <div className="text-sm font-semibold text-gray-800 mt-1">
                            {notif.montant?.toLocaleString()} FCFA
                          </div>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <button
                            className="text-blue-600 hover:underline text-xs font-semibold"
                            onClick={() => setSelectedNotif(notif)}
                          >
                            Détails
                          </button>
                          <button
                            className="text-yellow-700 hover:underline text-xs font-semibold"
                            onClick={() => onEditDepense(notif, setWaiting)}
                          >
                            Modifier
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            ) : (
              validated.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <span className="text-gray-400 text-4xl mb-3">🔔</span>
                  <p className="text-gray-600">Aucune notification de validation</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {validated.map((notif) => (
                    <div
                      key={notif.id}
                      className="relative bg-gradient-to-br from-green-50 to-white rounded-xl shadow-lg p-5 border-l-8 border-green-500 animate-fade-in transition-transform hover:scale-105 hover:shadow-2xl"
                    >
                      {newValidatedIds.has(notif.id) && (
                        <span className="absolute top-2 right-2 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">1</span>
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-green-500 text-xl">✓</span>
                        <span className="font-semibold text-sm">Validé par DG</span>
                      </div>
                      <div className="text-xs text-gray-500 mb-2 italic">
                        {(notif.dateValidationDG || notif.dateValidationCG || notif.dateValidationRT || notif.created)
                          ? new Date(notif.dateValidationDG || notif.dateValidationCG || notif.dateValidationRT || notif.created).toLocaleString()
                          : "-"}
                      </div>
                      <div className="bg-green-100 p-3 rounded-lg mb-3 border border-green-200">
                        <div className="font-bold text-sm text-green-700 truncate">{notif.rubrique}</div>
                        <div className="text-sm text-gray-600 truncate">{notif.description || "-"}</div>
                        <div className="text-sm font-semibold text-gray-800 mt-1">
                          {notif.montant?.toLocaleString()} FCFA
                        </div>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <button
                          className="text-blue-600 hover:underline text-xs font-semibold"
                          onClick={() => {
                            setSelectedValid(notif);
                            if (newValidatedIds.has(notif.id)) {
                              const updatedIds = new Set(newValidatedIds);
                              updatedIds.delete(notif.id);
                              setNewValidatedIds(updatedIds);
                            }
                          }}
                        >
                          Détails
                        </button>
                        <button
                          className="text-red-600 hover:underline text-xs font-semibold"
                          onClick={() => {
                            handleDeleteValid(notif);
                            if (newValidatedIds.has(notif.id)) {
                              const updatedIds = new Set(newValidatedIds);
                              updatedIds.delete(notif.id);
                              setNewValidatedIds(updatedIds);
                            }
                          }}
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
      {selectedNotif && renderDetailsModal(selectedNotif, "waiting")}
      {selectedValid && renderDetailsModal(selectedValid, "validated")}
    </div>
  );
};

export default ChefChantierDashboard;
